/**
 * Folder tree with checkboxes for STM32 code suite (redesigned).
 * Core is always included (locked). Drivers, peripherals, and RTOS are optional.
 * Full tree rendered on load. Parent checkbox toggles all children.
 * Shows descriptions, dependency badges, and collapsible sections.
 */

import React, { useMemo, useEffect, useRef, useState, useCallback, memo } from 'react'; // React core + hooks

/**
 * Icon component for node type (folder, file, lock, rtos)
 */
function NodeIcon({ type, isChecked }) {
  // Choose icon based on node type
  if (type === 'lock') {
    // Lock icon for required/core nodes
    return (
      <svg className="w-4 h-4 text-yellow-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    );
  }
  if (type === 'rtos') {
    // RTOS/refresh icon
    return (
      <svg className={`w-4 h-4 flex-shrink-0 ${isChecked ? 'text-green-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    );
  }
  if (type === 'folder') {
    // Folder icon
    return (
      <svg className="w-4 h-4 text-yellow-400/70 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
      </svg>
    );
  }
  // Default: file/chip icon
  return (
    <svg className={`w-4 h-4 flex-shrink-0 ${isChecked ? 'text-blue-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 3v2m6-2v2M9 19v2m6-2v2M3 9h2m-2 6h2m14-6h2m-2 6h2M7 7h10a2 2 0 012 2v6a2 2 0 01-2 2H7a2 2 0 01-2-2V9a2 2 0 012-2z" />
    </svg>
  );
}

/**
 * Left-border accent per top-level category, so each group reads as its own
 * unit. Keyed by node id; unknown categories fall back to neutral grey.
 */
const CATEGORY_ACCENT = {
  core: 'border-l-yellow-500/70',
  drivers: 'border-l-blue-500/70',
  peripherals: 'border-l-emerald-500/70',
  rtos: 'border-l-green-500/70',
  rtos_freertos: 'border-l-green-500/70',
};

/**
 * Single tree node (leaf or parent). Memoized to prevent unnecessary re-renders.
 */
const TreeNode = memo(function TreeNode({ node, nodes, selectedNodeIds, onToggle, level = 0 }) {
  const [expanded, setExpanded] = useState(true); // All sections expanded by default

  // Get child node objects
  const children = useMemo(() => {
    if (!node.children || !nodes) return []; // No children = leaf
    return node.children.map((id) => nodes[id]).filter(Boolean); // Map IDs to node objects
  }, [node.children, nodes]);

  const hasChildren = children.length > 0; // Is this a parent node?
  const isRequired = node.required; // Core nodes are required/locked
  const isChecked = isRequired || selectedNodeIds.has(node.id); // Checked state
  const isRtos = node.id === 'rtos_freertos' || node.id === 'rtos'; // RTOS node?

  // Calculate indeterminate state for parent checkboxes
  const isIndeterminate = useMemo(() => {
    if (!hasChildren) return false; // Leaves can't be indeterminate
    // Get all leaf descendants
    const getLeaves = (n) => {
      const kids = (n.children || []).map((id) => nodes[id]).filter(Boolean);
      if (kids.length === 0) return [n]; // This is a leaf
      return kids.flatMap(getLeaves); // Recurse into children
    };
    const leaves = children.flatMap(getLeaves); // All leaf descendants
    const selectableLeaves = leaves.filter((l) => l.selectable !== false && !l.required); // Only selectable leaves
    if (selectableLeaves.length === 0) return false; // No selectable leaves
    const checkedCount = selectableLeaves.filter((l) => selectedNodeIds.has(l.id)).length; // Count checked
    return checkedCount > 0 && checkedCount < selectableLeaves.length; // Some but not all = indeterminate
  }, [hasChildren, children, nodes, selectedNodeIds]);

  // Ref for checkbox indeterminate property (can't set via JSX attribute)
  const checkboxRef = useRef(null);
  useEffect(() => {
    if (checkboxRef.current) checkboxRef.current.indeterminate = isIndeterminate; // Set indeterminate on DOM element
  }, [isIndeterminate]);

  // Handle checkbox click
  const handleCheckboxChange = useCallback(() => {
    if (isRequired) return; // Can't uncheck required nodes
    onToggle(node, hasChildren, children, nodes); // Delegate to parent handler
  }, [node, hasChildren, children, nodes, isRequired, onToggle]);

  // Determine icon type
  const iconType = isRequired ? 'lock' : isRtos ? 'rtos' : hasChildren ? 'folder' : 'chip';

  // Don't render root node label, just its children
  if (node.id === 'root') {
    return (
      <div className="space-y-1">
        {children.map((child) => (
          <TreeNode
            key={child.id}
            node={child}
            nodes={nodes}
            selectedNodeIds={selectedNodeIds}
            onToggle={onToggle}
            level={0}
          />
        ))}
      </div>
    );
  }

  // Skip rtos parent node, show rtos_freertos directly
  if (node.id === 'rtos') {
    return (
      <>
        {children.map((child) => (
          <TreeNode
            key={child.id}
            node={child}
            nodes={nodes}
            selectedNodeIds={selectedNodeIds}
            onToggle={onToggle}
            level={level}
          />
        ))}
      </>
    );
  }

  // Top-level nodes render as their own contained card so the categories
  // (Core / Drivers / Peripherals / …) are visually separable at a glance,
  // instead of blending into one flat list.
  const isTopGroup = level === 0;
  const accent = CATEGORY_ACCENT[node.id] || 'border-l-gray-600/70';

  return (
    <div
      className={
        isTopGroup
          ? `mb-3 overflow-hidden rounded-lg border border-gray-700/70 border-l-[3px] ${accent} bg-gray-800/20`
          : ''
      }
    >
      {/* Node row — a tinted header when this is a top-level category */}
      <div
        className={`flex items-center gap-2 transition-colors group ${
          isTopGroup
            ? `px-3 py-2 bg-gray-800/50 ${hasChildren && expanded ? 'border-b border-gray-700/50' : ''}`
            : `px-2 py-1.5 rounded-lg ${isChecked && !isRequired ? 'bg-blue-900/10' : 'hover:bg-gray-800/50'}`
        }`}
        style={isTopGroup ? undefined : { paddingLeft: `${level * 20 + 8}px` }} // Indent inner rows
      >
        {/* Expand/collapse arrow for parents */}
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)} // Toggle expanded
            className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-300 transition-colors flex-shrink-0"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            <svg
              className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        ) : (
          <div className="w-5 flex-shrink-0" /> /* Spacer for leaf alignment */
        )}

        {/* Checkbox */}
        <input
          type="checkbox"
          ref={checkboxRef}
          checked={isChecked}
          onChange={handleCheckboxChange}
          disabled={isRequired}
          className={`w-4 h-4 rounded border flex-shrink-0 ${
            isRequired
              ? 'border-yellow-600 bg-yellow-900/30 text-yellow-500 cursor-not-allowed'
              : 'border-gray-600 bg-gray-800 text-blue-500 cursor-pointer focus:ring-blue-500 focus:ring-offset-0 focus:ring-1'
          }`}
        />

        {/* Icon */}
        <NodeIcon type={iconType} isChecked={isChecked} />

        {/* Label + description */}
        <div className="flex-1 min-w-0">
          <span className={
            isTopGroup
              ? 'text-sm font-semibold text-silver-light uppercase tracking-wide'
              : `text-sm font-medium ${isRequired ? 'text-yellow-300/80' : isChecked ? 'text-silver-light' : 'text-gray-400'}`
          }>
            {node.label}
          </span>
          {/* Description text */}
          {node.description && (
            <span className="text-xs text-gray-500 ml-2 hidden sm:inline">
              — {node.description}
            </span>
          )}
        </div>

        {/* Badges */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {isRequired && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-900/30 text-yellow-500 font-medium">
              REQUIRED
            </span>
          )}
          {node.dependsOn && node.dependsOn.length > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-500 font-medium hidden md:inline" title={`Depends on: ${node.dependsOn.join(', ')}`}>
              needs {node.dependsOn.map((d) => {
                const dep = nodes[d]; // Look up dependency node
                return dep?.label || d; // Show label or ID
              }).join(', ')}
            </span>
          )}
        </div>
      </div>

      {/* Children (if expanded) */}
      {hasChildren && expanded && (
        <div className={isTopGroup ? 'py-1.5' : ''}>
          {children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              nodes={nodes}
              selectedNodeIds={selectedNodeIds}
              onToggle={onToggle}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
});

/**
 * Main FolderTree component. Renders the full tree with checkboxes.
 */
export default function FolderTree({ tree, selectedNodeIds, onSelectionChange }) {
  const rootId = tree?.rootId; // Root node ID
  const nodes = useMemo(() => tree?.nodes || {}, [tree]);
  const root = rootId ? nodes[rootId] : null; // Root node object

  // Debug log
  useEffect(() => {
    console.log(`[${new Date().toISOString()}] [FolderTree] render`, { nodeCount: Object.keys(nodes).length, selectedCount: selectedNodeIds.size });
  });

  /**
   * Handle toggle for a node. If parent, toggle all selectable leaf descendants.
   * If leaf, toggle just itself.
   */
  const handleToggle = useCallback((node, hasChildren, children, allNodes) => {
    const next = new Set(selectedNodeIds); // Clone current selection

    if (hasChildren) {
      // Get all selectable leaf descendants
      const getSelectableLeaves = (n) => {
        const kids = (n.children || []).map((id) => allNodes[id]).filter(Boolean);
        if (kids.length === 0) {
          // Leaf node
          return (n.selectable !== false && !n.required) ? [n.id] : [];
        }
        return kids.flatMap(getSelectableLeaves); // Recurse
      };
      const leafIds = children.flatMap(getSelectableLeaves); // All selectable leaves under this parent
      const allSelected = leafIds.every((id) => selectedNodeIds.has(id)); // Are all currently selected?

      if (allSelected) {
        // Deselect all
        leafIds.forEach((id) => next.delete(id));
      } else {
        // Select all
        leafIds.forEach((id) => next.add(id));
      }
    } else {
      // Leaf toggle
      if (next.has(node.id)) {
        next.delete(node.id); // Uncheck
      } else {
        next.add(node.id); // Check
      }
    }

    console.log(`[${new Date().toISOString()}] [FolderTree] toggle`, { nodeId: node.id, newCount: next.size });
    onSelectionChange(next); // Propagate to parent
  }, [selectedNodeIds, onSelectionChange]);

  // Quick-select buttons: Select All / Deselect All for convenience
  const handleSelectAll = useCallback(() => {
    const next = new Set(); // Fresh set
    Object.values(nodes).forEach((n) => {
      if (n.selectable !== false && !n.required && !n.children) {
        next.add(n.id); // Add all selectable leaves
      }
    });
    console.log(`[${new Date().toISOString()}] [FolderTree] selectAll`, { count: next.size });
    onSelectionChange(next);
  }, [nodes, onSelectionChange]);

  const handleDeselectAll = useCallback(() => {
    console.log(`[${new Date().toISOString()}] [FolderTree] deselectAll`);
    onSelectionChange(new Set()); // Empty set
  }, [onSelectionChange]);

  if (!root) return null; // No tree data

  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900 overflow-hidden">
      {/* Header with quick actions */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-800/50 border-b border-gray-700">
        <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">
          Project Structure
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSelectAll}
            className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
          >
            Select All
          </button>
          <span className="text-gray-600">|</span>
          <button
            type="button"
            onClick={handleDeselectAll}
            className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
          >
            Deselect All
          </button>
        </div>
      </div>

      {/* Tree body */}
      <div className="p-3">
        <TreeNode
          node={root}
          nodes={nodes}
          selectedNodeIds={selectedNodeIds}
          onToggle={handleToggle}
          level={0}
        />
      </div>

      {/* Footer: selection count */}
      <div className="px-4 py-2 bg-gray-800/30 border-t border-gray-700 flex items-center justify-between">
        <span className="text-xs text-gray-500">
          {selectedNodeIds.size} module{selectedNodeIds.size !== 1 ? 's' : ''} selected
        </span>
        <span className="text-xs text-gray-600">
          Core (startup, system, CMSIS) is always included
        </span>
      </div>
    </div>
  );
}
